import React, { useEffect, useState } from 'react';
import { Form, Button, Card, InputGroup, Spinner, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaEye, FaInfoCircle } from 'react-icons/fa';
import { Formik, Form as FForm } from 'formik';
import { string, object, InferType } from 'yup'; // for only what you need
import { useWhyDidYouUpdate } from '../../hooks';
import withSizes from 'react-sizes';
import { useHistory } from 'react-router-dom';
// import { AppStore } from 'store/app-store';
import { useRootStore } from 'store';
import { useLazyQuery } from '@apollo/client';
import { COLLECTIONPOINT_LOGIN } from './graphql';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ChristmasTree } from 'components/common/christmas-tree';
import { saveBrowserLoginCredentials } from './save-browser-login-credentials';
import './login-form.scss';

/**
 * Setup Yup Validation
 */
const validationSchema = object().shape({
  vatNumber: string().required(),
});

/** Οδηγία συμπλήρωσης αριθμού Δελτίου Αποστολής */
const DISPATCH_NOTE_INFO =
  'Γράψτε τα 5 αριθμητικά ψηφία ενός πρόσφατου Δελτίου Αποστολής. π.χ. αν το δελτίο αναφέρει ΔΑ-00123 εσείς συμπληρώστε το 00123';

export type FormDataType = InferType<typeof validationSchema>;

type Props = {
  message?: string | null;
  loading?: boolean;
  onSubmit: (values, action) => void;
  isMobile?: boolean;
};

/** Συγχρονισμός browser autofill με Formik — το autofill δεν ενεργοποιεί onChange */
const AutofillSync = ({
  values,
  setFieldValue,
}: {
  values: { vatNumber: string; dispatchNote: string };
  setFieldValue: (field: string, value: string, shouldValidate?: boolean) => void;
}) => {
  useEffect(() => {
    const syncFromDom = () => {
      const vatEl = document.getElementById('vatNumber') as HTMLInputElement | null;
      const dispatchEl = document.getElementById('dispatchNote') as HTMLInputElement | null;

      if (vatEl?.value && vatEl.value !== values.vatNumber) {
        setFieldValue('vatNumber', vatEl.value, true);
      }
      if (dispatchEl?.value && dispatchEl.value !== values.dispatchNote) {
        setFieldValue('dispatchNote', dispatchEl.value, true);
      }
    };

    syncFromDom();
    const timers = [100, 300, 800].map((ms) => setTimeout(syncFromDom, ms));
    return () => timers.forEach(clearTimeout);
  }, [setFieldValue, values.dispatchNote, values.vatNumber]);

  return null;
};

const LoginForm = ({ message = '', loading = false, onSubmit, isMobile }: Props) => {
  /*-------------------- Body -----------------------------------------------*/
  const { appStore } = useRootStore();
  const [errorMsg, setErrorMsg] = useState('');
  const [showDispatchNote, setShowDispatchNote] = useState(false);

  useWhyDidYouUpdate('[LoginForm]', { message, onSubmit });
  const history = useHistory();

  // const [loginQuery, { loading: queryLoading, data, error }] = useLazyQuery(COLLECTIONPOINT_LOGIN);

  const [loginQuery, { loading: queryLoading }] = useLazyQuery(COLLECTIONPOINT_LOGIN, {
    errorPolicy: 'all',
    onCompleted: (data) => {
      console.log('[LoginForm] loginQuery completed with data:', data);

      if (data?.collectionpointLoginByVat) {
        runInAction(() => {
          appStore.login(
            data.collectionpointLoginByVat.collectionpointId,
            data.collectionpointLoginByVat,
          );
        });
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        // Login failed – user doesn't exist
        setErrorMsg('Δε βρέθηκε σημείο συλλογής.');
      }
    },

    onError: (error) => {
      // Handle error for backend login failure
      console.log('[LoginForm] loginQuery completed with error:', error);
      const msg = error.graphQLErrors?.[0]?.message || 'Σφάλμα σύνδεσης.';
      setErrorMsg(msg);
    },
  });

  // useEffect(() => {
  //   if (data?.collectionpointLogin) {
  //     runInAction(() => {
  //       appStore.login(data.collectionpointLogin.collectionpointId, data.collectionpointLogin);
  //     });
  //     history.replace('/home');
  //   }
  // }, [data]);

  /*-------------------- Render ---------------------------------------------*/

  return (
    <Formik
      initialValues={{ vatNumber: '', dispatchNote: '' }}
      validationSchema={validationSchema}
      onSubmit={({ vatNumber, dispatchNote }) => {
        // Κλήση μέσα στο user gesture ώστε ο browser να αποθηκεύσει τα στοιχεία
        void saveBrowserLoginCredentials(vatNumber, dispatchNote);
        loginQuery({ variables: { vat: vatNumber, dispatchNote: dispatchNote } });
      }}
    >
      {(formikBag) => (
        <div>
          <AutofillSync values={formikBag.values} setFieldValue={formikBag.setFieldValue} />
          <ChristmasTree />
          <Card
            className="bg-light shadow-sm p-3 mb-5 login-size w-100"
            style={{
              maxWidth: isMobile ? '100%' : '640px',
            }}
          >
            <Card.Body>
              <Card.Title className="text-center mb-4 mt-1" as="h4">
                <img
                  alt=""
                  src={process.env.PUBLIC_URL + '/logo.png'}
                  className="mx-auto d-block"
                  style={{
                    height: '6rem',
                  }}
                />
                <h1
                  style={{ color: '#006EB6', fontSize: isMobile ? '1.1rem' : '1.6rem' }}
                  className="mt-2 mb-5 d-flex align-items-center justify-content-center text-center not-found-container"
                >
                  <span>Φωτοκύκλωση α.ε.</span>
                </h1>
              </Card.Title>

              <Form as={FForm} autoComplete="on" className="login-vat-form">
                {/* Field 1 ---------------------------------------------------------------  */}
                <Form.Group>
                  <InputGroup className="login-input-group">
                    <InputGroup.Text>Α.Φ.Μ.</InputGroup.Text>
                    <Form.Control
                      id="vatNumber"
                      type="text"
                      placeholder="Σύνδεση με Α.Φ.Μ."
                      {...formikBag.getFieldProps('vatNumber')}
                      name="username"
                      autoComplete="username"
                      onChange={formikBag.handleChange('vatNumber')}
                      isInvalid={!!formikBag.touched.vatNumber && !!formikBag.errors.vatNumber}
                    />
                  </InputGroup>
                  {!!formikBag.touched.vatNumber && !!formikBag.errors.vatNumber && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      Το ΑΦΜ απαιτείται. Δεν μπορεί να είναι κενό
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {/* Field 2  ------------------------------------------------------------ */}
                <Form.Group>
                  <div className="position-relative">
                    <InputGroup className="login-input-group login-input-group--with-append">
                      <InputGroup.Text style={{ minWidth: '3.8rem' }}>Δ.Α.</InputGroup.Text>
                      <Form.Control
                        className="pr-5"
                        id="dispatchNote"
                        type={showDispatchNote ? 'text' : 'password'}
                        inputMode="numeric"
                        placeholder="Τα αριθμητικά ψηφία ενός πρόσφατου Δελτίου Αποστολής"
                        {...formikBag.getFieldProps('dispatchNote')}
                        name="password"
                        autoComplete="current-password"
                        onChange={formikBag.handleChange('dispatchNote')}
                        isInvalid={
                          !!formikBag.touched.dispatchNote && !!formikBag.errors.dispatchNote
                        }
                      />
                      <InputGroup.Append>
                        <Button
                          variant="secondary"
                          type="button"
                          className="login-input-action-btn"
                          aria-label={
                            showDispatchNote ? 'Απόκρυψη ψηφίων Δελτίου' : 'Εμφάνιση ψηφίων Δελτίου'
                          }
                          onClick={() => setShowDispatchNote((value) => !value)}
                        >
                          <FaEye />
                        </Button>
                      </InputGroup.Append>
                    </InputGroup>
                    <OverlayTrigger
                      trigger="click"
                      rootClose
                      placement="top"
                      overlay={
                        <Popover id="dispatch-note-info">
                          <Popover.Content className="text-left" style={{ maxWidth: '18rem' }}>
                            {DISPATCH_NOTE_INFO}
                          </Popover.Content>
                        </Popover>
                      }
                    >
                      <span
                        className="position-absolute text-info"
                        style={{
                          // Αριστερά από το κουμπί ματάκι, όπως πριν μέσα στο πεδίο
                          right: 'calc(42px + 0.75rem)',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          zIndex: 5,
                          lineHeight: 1,
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Πληροφορίες Δελτίου Αποστολής"
                      >
                        <FaInfoCircle />
                      </span>
                    </OverlayTrigger>
                  </div>

                  {/* Error Line */}
                  {errorMsg && (
                    <Form.Control.Feedback
                      type="invalid"
                      className="d-block text-danger mt-2 font-weight-bold"
                    >
                      Σφάλμα: {errorMsg}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Button
                  block
                  variant="primary"
                  size="sm"
                  style={{ fontSize: '0.8rem' }}
                  type="submit"
                  disabled={
                    !(formikBag.isValid && formikBag.values.vatNumber.trim() && !queryLoading)
                  }
                >
                  {queryLoading && <Spinner animation="border" size="sm" className="mx-4" />}
                  <span>{'Είσοδος'}</span>
                </Button>
              </Form>

              {/* {appStore.allowLeadAcidRegistration && (
              <Button
                className="mt-3"
                block
                variant="success"
                size="sm"
                style={{ fontSize: '0.8rem' }}
                onClick={() => {
                  history.push('/application');
                }}
              >
                Εγγραφή νέου χρήστη κατόχου μπαταριών μολύβδου
              </Button>
            )} */}
            </Card.Body>
          </Card>
        </div>
      )}
    </Formik>
  );
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 768,
});
export default withSizes(mapSizesToProps)(observer(LoginForm));
